import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { LockKeyhole, Search } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  Input,
  Label,
  toast,
} from '@exiftrator/shared/components/ui';
import { usePresets } from '../../../contexts/PresetsContext';
import { useFeatureAccess } from '../../../contexts/LicenseContext';
import { useBasePath } from '../../../contexts/BasePathContext';
import {
  CATEGORY_METADATA,
  DEFAULT_CATEGORY_ORDER,
  EXIF_FIELDS,
} from '../../../config/fieldDefinitions';
import { EmptyState } from '../../EmptyState';
import { cn } from '../../utils/cn';
import PresetCard from './PresetCard';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import PresetFieldValueInput from '../components/PresetFieldValueInput';

const EMPTY_FORM = {
  name: '',
  includedFields: [],
  values: {},
};

function fieldMatchesQuery(field, normalizedQuery) {
  if (!normalizedQuery) {
    return true;
  }

  return (
    field.label.toLowerCase().includes(normalizedQuery) ||
    field.id.toLowerCase().includes(normalizedQuery)
  );
}

function PresetSettings({ editorMode = null, presetId = null }) {
  const navigate = useNavigate();
  const { appPath } = useBasePath();
  const presetsAccess = useFeatureAccess('presets');
  const { getAllPresets, addPreset, updatePreset, deletePreset } = usePresets();

  const [activePresetId, setActivePresetId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [fieldSearchQuery, setFieldSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const allPresets = getAllPresets();

  const filteredPresets = useMemo(() => {
    return allPresets.filter((preset) => {
      const hasOnlyGps =
        preset.includedFields.every((fieldId) => fieldId === 'latitude' || fieldId === 'longitude') &&
        preset.includedFields.length > 0;
      return !hasOnlyGps;
    });
  }, [allPresets]);

  const isCreateMode = editorMode === 'new';
  const isEditMode = editorMode === 'edit';
  const isEditorPage = isCreateMode || isEditMode;
  const activePreset = useMemo(
    () => allPresets.find((preset) => String(preset.id) === String(presetId)) || null,
    [allPresets, presetId],
  );
  const normalizedFieldQuery = fieldSearchQuery.trim().toLowerCase();

  const allFieldGroups = useMemo(() => {
    const fields = Object.values(EXIF_FIELDS);

    return DEFAULT_CATEGORY_ORDER.map((categoryId) => {
      const category = CATEGORY_METADATA[categoryId];
      if (!category) {
        return null;
      }

      const categoryFields = fields.filter(
        (field) => field.category === categoryId && fieldMatchesQuery(field, normalizedFieldQuery),
      );

      if (categoryFields.length === 0) {
        return null;
      }

      return {
        category,
        fields: categoryFields,
      };
    }).filter(Boolean);
  }, [normalizedFieldQuery]);

  const selectedFieldIdSet = useMemo(() => new Set(formData.includedFields), [formData.includedFields]);

  const selectedFieldDetails = useMemo(() => {
    return formData.includedFields
      .map((fieldId) => EXIF_FIELDS[fieldId])
      .filter(Boolean)
      .filter((field) => fieldMatchesQuery(field, normalizedFieldQuery));
  }, [formData.includedFields, normalizedFieldQuery]);

  const resetEditorState = useCallback(() => {
    setActivePresetId(null);
    setFieldSearchQuery('');
    setFormData(EMPTY_FORM);
  }, []);

  const closeEditor = useCallback(() => {
    resetEditorState();
    navigate(appPath('/settings/presets'));
  }, [appPath, navigate, resetEditorState]);

  useEffect(() => {
    if (isCreateMode) {
      setActivePresetId(null);
      setFieldSearchQuery('');
      setFormData(EMPTY_FORM);
      return;
    }

    if (isEditMode && activePreset) {
      setActivePresetId(activePreset.id);
      setFieldSearchQuery('');
      setFormData({
        name: activePreset.name,
        includedFields: [...(activePreset.includedFields || [])],
        values: { ...(activePreset.values || {}) },
      });
    }
  }, [activePreset, isCreateMode, isEditMode]);

  const handleCreateNew = useCallback(() => {
    if (!presetsAccess.allowed) {
      return;
    }

    navigate(appPath('/settings/presets/new'));
  }, [appPath, navigate, presetsAccess.allowed]);

  const handleEdit = useCallback(
    (preset) => {
      if (!presetsAccess.allowed) {
        return;
      }

      navigate(appPath(`/settings/presets/edit/${encodeURIComponent(String(preset.id))}`));
    },
    [appPath, navigate, presetsAccess.allowed],
  );

  const handleDeleteRequest = useCallback((preset) => {
    setDeleteTarget(preset);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) {
      return;
    }

    deletePreset(deleteTarget.id);

    if (activePresetId === deleteTarget.id) {
      resetEditorState();
    }

    setDeleteTarget(null);
  }, [activePresetId, deletePreset, deleteTarget, resetEditorState]);

  const handleSubmit = useCallback(() => {
    const trimmedName = formData.name.trim();

    if (!trimmedName) {
      toast.error('Please enter a preset name.');
      return;
    }

    if (formData.includedFields.length === 0) {
      toast.error('Please select at least one field.');
      return;
    }

    const payload = {
      name: trimmedName,
      includedFields: formData.includedFields,
      values: formData.values,
    };

    if (isEditMode && activePresetId !== null) {
      updatePreset(activePresetId, payload);
      toast.success('Preset updated.');
    } else {
      addPreset(payload.name, payload.includedFields, payload.values);
      toast.success('Preset created.');
    }

    closeEditor();
  }, [activePresetId, addPreset, closeEditor, formData, isEditMode, updatePreset]);

  const toggleFieldSelection = useCallback((fieldId) => {
    setFormData((previous) => {
      const alreadySelected = previous.includedFields.includes(fieldId);
      const nextIncludedFields = alreadySelected
        ? previous.includedFields.filter((id) => id !== fieldId)
        : [...previous.includedFields, fieldId];

      const nextValues = { ...previous.values };
      if (alreadySelected) {
        delete nextValues[fieldId];
      }

      return {
        ...previous,
        includedFields: nextIncludedFields,
        values: nextValues,
      };
    });
  }, []);

  const updateFieldValue = useCallback((fieldId, value) => {
    setFormData((previous) => ({
      ...previous,
      values: {
        ...previous.values,
        [fieldId]: value,
      },
    }));
  }, []);

  if (isEditorPage && !presetsAccess.allowed) {
    return (
      <div className="space-y-4">
        <Alert className="border-accent-primary/30 bg-accent-primary/10">
          <LockKeyhole className="size-4 text-accent-primary" />
          <AlertTitle>Presets are a Pro feature</AlertTitle>
          <AlertDescription>
            Unlock Pro to create and edit presets.
          </AlertDescription>
        </Alert>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={closeEditor}>
            Back to presets
          </Button>
          <Button type="button" onClick={() => navigate(appPath('/settings/license'))}>
            Unlock Pro
          </Button>
        </div>
      </div>
    );
  }

  if (isEditorPage) {
    if (isEditMode && !activePreset) {
      return (
        <div className="space-y-4">
          <div>
            <h2 className="font-sans text-[24px] font-bold text-text-primary">
              Preset not found
            </h2>
            <p className="mt-1 text-sm text-text-supporting">
              This preset may have been deleted or is no longer available.
            </p>
          </div>
          <Button type="button" variant="outline" onClick={closeEditor}>
            Back to presets
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6 pb-8">
        <div className="flex flex-col gap-4 border-b border-border-default pb-5 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="font-sans text-[24px] font-bold text-text-primary">
              {isEditMode ? 'Edit Preset' : 'Create Preset'}
            </h2>
            <p className="mt-1 text-sm text-text-supporting">
              Select fields and set values directly in each row.
            </p>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button type="button" variant="outline" onClick={closeEditor}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit}>
              {isEditMode ? 'Save Preset' : 'Create Preset'}
            </Button>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="preset-name" className="text-xs text-text-supporting">
            Preset name
          </Label>
          <Input
            id="preset-name"
            value={formData.name}
            onChange={(event) => {
              const nextName = event.target.value;
              setFormData((previous) => ({ ...previous, name: nextName }));
            }}
            placeholder="My metadata preset"
            autoFocus
          />
        </div>

        <section className="space-y-4 rounded-lg border border-border-default/70 bg-bg-primary/50 p-4">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-text-primary">Preset field builder</p>
            <p className="text-xs text-text-supporting">
              Check a field to include it, then set its value in the same row.
            </p>
          </div>

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-supporting" />
            <Input
              value={fieldSearchQuery}
              onChange={(event) => setFieldSearchQuery(event.target.value)}
              placeholder="Search fields"
              className="pl-9"
            />
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-text-supporting">
                Selected fields ({formData.includedFields.length})
              </p>

              {selectedFieldDetails.length === 0 ? (
                <p className="rounded-md border border-dashed border-border-default p-3 text-xs text-text-supporting">
                  {formData.includedFields.length === 0
                    ? 'Select fields below to start building this preset.'
                    : 'No selected fields match this search.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {selectedFieldDetails.map((field) => (
                    <PresetBuilderRow
                      key={`selected-${field.id}`}
                      field={field}
                      checked
                      value={formData.values[field.id]}
                      onToggle={() => toggleFieldSelection(field.id)}
                      onValueChange={(value) => updateFieldValue(field.id, value)}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-text-supporting">
                All fields
              </p>

              {allFieldGroups.length === 0 ? (
                <p className="rounded-md border border-dashed border-border-default p-3 text-xs text-text-supporting">
                  {normalizedFieldQuery
                    ? 'No fields match this search.'
                    : 'No fields are available.'}
                </p>
              ) : (
                allFieldGroups.map(({ category, fields }) => (
                  <div key={category.id} className="space-y-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-text-supporting">
                      {category.label}
                    </p>
                    <div className="space-y-2">
                      {fields.map((field) => (
                        <PresetBuilderRow
                          key={field.id}
                          field={field}
                          checked={selectedFieldIdSet.has(field.id)}
                          value={formData.values[field.id]}
                          onToggle={() => toggleFieldSelection(field.id)}
                          onValueChange={(value) => updateFieldValue(field.id, value)}
                        />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {!presetsAccess.allowed ? (
        <Alert className="mb-4 border-accent-primary/30 bg-accent-primary/10">
          <LockKeyhole className="size-4 text-accent-primary" />
          <AlertTitle>Presets are a Pro feature</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>Unlock Pro to create, edit, and apply presets.</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(appPath('/settings/license'))}
            >
              Unlock Pro
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className={!presetsAccess.allowed ? 'pointer-events-none select-none opacity-60' : ''}>
        <div className="mb-6 flex items-center justify-between gap-3">
          <h2 className="font-sans text-[24px] font-bold text-text-primary">Manage Presets</h2>
          <Button
            type="button"
            className="h-9 px-5 text-[11px] font-semibold uppercase tracking-[0.12em]"
            onClick={handleCreateNew}
          >
            New Preset
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto pr-4 pb-8">
          {filteredPresets.length === 0 ? (
            <EmptyState title='No presets saved yet.' description='Create a preset to quickly apply metadata values.' />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredPresets.map((preset) => (
                <PresetCard
                  key={preset.id}
                  preset={preset}
                  onEdit={handleEdit}
                  onDelete={handleDeleteRequest}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
          }
        }}
        itemLabel={deleteTarget?.name}
        title="Delete preset"
        confirmLabel="Delete preset"
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}

function PresetBuilderRow({
  field,
  checked,
  value,
  onToggle,
  onValueChange,
}) {
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-3 rounded-md border p-3 md:grid-cols-[minmax(0,230px)_1fr] md:items-center',
        checked ? 'border-border-default bg-bg-secondary' : 'border-border-subtle bg-bg-primary/35',
      )}
    >
      <label className="flex cursor-pointer items-start gap-2">
        <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-text-primary">{field.label}</p>
          <p className="text-[11px] text-text-supporting">
            {CATEGORY_METADATA[field.category]?.label || 'Field'}
          </p>
        </div>
      </label>

      <div className={cn(!checked && 'opacity-70')}>
        <PresetFieldValueInput
          field={field}
          value={value}
          onChange={onValueChange}
          disabled={!checked}
        />
      </div>
    </div>
  );
}

export default PresetSettings;
